import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { ConfluenceExtension } from '@atlassian/forge-ui-types';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import cloneDeep from 'lodash/cloneDeep';
import { Box, xcss } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { getAkModalWidth } from '../utils';
import { useSendNodeCountAnalytics } from '../utils/useSendNodeCountAnalytics';
import { RendererContext } from '../context';
var ModalWrapperStyles = xcss({
  paddingBottom: 'space.300'
});
export var NeverLoads = /*#__PURE__*/React.lazy(function () {
  return new Promise(function () {});
});
export var getComponent = function getComponent(componentMap, componentType) {
  if (componentType === 'User' || componentType === 'Avatar') {
    return componentMap['User'] || componentMap['Avatar'];
  }
  if (componentType === 'UserGroup' || componentType === 'AvatarStack') {
    return componentMap['UserGroup'] || componentMap['AvatarStack'];
  }
  return componentMap[componentType];
};
var isAlignFormButtonsEnd = function isAlignFormButtonsEnd(extensionType, forgeDoc) {
  // Confluence Byline and ContextMenu Forge apps with a Form component as
  // there only child shifts the form buttons to the end in the footer
  return (extensionType === ConfluenceExtension.BYLINE || extensionType === ConfluenceExtension.CONTEXT_MENU) && forgeDoc.children[0].type === 'Form';
};
var forgeDocWithModalComponent = function forgeDocWithModalComponent(forgeDoc, modalExtension) {
  var closeModalExtension = modalExtension.closeModalExtension,
    title = modalExtension.title,
    modalWidth = modalExtension.modalWidth,
    onSubmitSuccess = modalExtension.onSubmitSuccess;

  // ModalDialog component sets the default width to `medium`
  var akModalWidth = modalWidth ? getAkModalWidth(modalWidth) : undefined;
  var forgeDocWithModal = _objectSpread(_objectSpread({}, forgeDoc), {}, {
    children: [{
      type: 'ModalDialog',
      props: {
        onClose: closeModalExtension,
        header: title,
        width: akModalWidth,
        onSubmitSuccess: onSubmitSuccess
      },
      children: forgeDoc.children
    }]
  });
  return forgeDocWithModal;
};
var forgeDocWithFormEndButtons = function forgeDocWithFormEndButtons(forgeDoc) {
  return _objectSpread(_objectSpread({}, forgeDoc), {}, {
    children: [_objectSpread(_objectSpread({}, forgeDoc.children[0]), {}, {
      props: _objectSpread(_objectSpread({}, forgeDoc.children[0].props), {}, {
        alignFooterButtons: 'end'
      })
    })]
  });
};
export var RendererNext = function RendererNext(_ref) {
  var forgeDoc = _ref.forgeDoc,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    error = _ref.error,
    customRenderErrorFn = _ref.customRenderErrorFn,
    components = _ref.components,
    dispatch = _ref.dispatch,
    modalExtension = _ref.modalExtension,
    extensionType = _ref.extensionType,
    isNative = _ref.isNative;
  var _useContext = useContext(RendererContext),
    forgeEnvironment = _useContext.forgeEnvironment;
  useSendNodeCountAnalytics({
    forgeDoc: forgeDoc,
    forgeEnvironment: forgeEnvironment !== null && forgeEnvironment !== void 0 ? forgeEnvironment : 'DEVELOPMENT',
    UIKitVersion: isNative ? '2' : '1'
  });
  var render = function render(forgeDoc) {
    var renderFn = getComponent(components, forgeDoc.type);
    if (!renderFn) {
      if (customRenderErrorFn) {
        customRenderErrorFn(forgeDoc.type);
      } else {
        throw new Error("Error rendering app - encountered unknown component ".concat(forgeDoc.type, "."));
      }
    }
    return renderFn({
      forgeDoc: forgeDoc,
      dispatch: dispatch,
      render: render
    });
  };

  // true while fetching initial forgeDoc
  if (loading && !forgeDoc) {
    return /*#__PURE__*/React.createElement(NeverLoads, null);
  }
  if (error) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, components.ErrorPanel({
      dispatch: dispatch,
      forgeDoc: {
        type: 'ErrorPanel',
        props: {
          error: {
            name: 'Error',
            message: 'Something Went Wrong',
            errorMessage: error
          }
        },
        children: []
      },
      render: render
    }));
  }
  if (!forgeDoc) {
    return null;
  }

  // Extensions rendered in a modal for version 10 and onwards are handled here.
  // This is the default behavior for handles model extensions across product modules.
  // When the modelExtension prop is present, and the forgeReactMajorVersion is 10 or higher,
  // the provided foreDoc is rendered within a Modal component with the provided modalExtension props.
  // This setup is to ensure that the modal extension is rendered correctly across all product modules,
  // and is the resolution for the HOT described in: https://ops.internal.atlassian.com/jira/browse/HOT-108146
  if (forgeDoc.forgeReactMajorVersion && forgeDoc.forgeReactMajorVersion >= 10 && modalExtension) {
    var closeModalExtension = modalExtension.closeModalExtension,
      title = modalExtension.title,
      modalWidth = modalExtension.modalWidth,
      onSubmitSuccess = modalExtension.onSubmitSuccess;
    var isFirstChildAForm = forgeDoc.children[0].type === 'Form';

    // injects onSubmitSuccess to into the Form component submit handler
    if (isFirstChildAForm) {
      // doing a deep clone here to make a copy of onSubmit
      var formPropsCopy = cloneDeep(forgeDoc.children[0].props);
      forgeDoc.children[0].props = _objectSpread(_objectSpread({}, formPropsCopy), {}, {
        onSubmit: function () {
          var _onSubmit = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
            return _regeneratorRuntime.wrap(function _callee$(_context) {
              while (1) switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return formPropsCopy.onSubmit();
                case 2:
                  if (!onSubmitSuccess) {
                    _context.next = 5;
                    break;
                  }
                  _context.next = 5;
                  return onSubmitSuccess();
                case 5:
                case "end":
                  return _context.stop();
              }
            }, _callee);
          }));
          function onSubmit() {
            return _onSubmit.apply(this, arguments);
          }
          return onSubmit;
        }()
      });
    }
    var akModalWidth = modalWidth ? getAkModalWidth(modalWidth) : undefined;
    return /*#__PURE__*/React.createElement(Modal, {
      onClose: closeModalExtension,
      width: akModalWidth
    }, /*#__PURE__*/React.createElement(ModalHeader, null, /*#__PURE__*/React.createElement(ModalTitle, null, title), /*#__PURE__*/React.createElement(IconButton, {
      appearance: "subtle",
      icon: CrossIcon,
      label: "Close Modal",
      onClick: closeModalExtension
    })), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Box, {
      xcss: ModalWrapperStyles
    }, render(forgeDoc))));
  }
  var newForgeDoc = extensionType && isAlignFormButtonsEnd(extensionType, forgeDoc) ? forgeDocWithFormEndButtons(forgeDoc) : forgeDoc;
  var extensionForgeDoc = modalExtension ? forgeDocWithModalComponent(newForgeDoc, modalExtension) : newForgeDoc;
  return /*#__PURE__*/React.createElement(React.Fragment, null, render(extensionForgeDoc));
};